import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import '../../DashboardHome/knowledge.css';

const ICN = {
    search: (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    caret: (
        <svg viewBox="0 0 10 10" fill="none">
            <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    plus: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    upload: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 11V4m0 0L4 7m3-3l3 3M2 2h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    download: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 2v7m0 0l-3-3m3 3l3-3M2 11v1h10v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    layers: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M7 2l5 2.5L7 7 2 4.5 7 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
            <path d="M2 7.5L7 10l5-2.5M2 10l5 2.5L12 10" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
    ),
    bolt: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M8 1L3 8h3l-1 5 5-7H7l1-5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
        </svg>
    ),
    clock: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.3" />
            <path d="M7 4v3l2 1.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
    ),
    globe: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.3" />
            <path d="M2 7h10M7 2c1.8 1.6 1.8 8.4 0 10M7 2c-1.8 1.6-1.8 8.4 0 10" stroke="currentColor" strokeWidth="1.3" />
        </svg>
    ),
};

const PAIRS = ['Any pair'];
const THRESH = ['≥ 70%', '≥ 80%', '≥ 90%', '100% only'];
const SORT = ['Match %', 'Most used', 'Recently edited'];

const threshNum = (s) => parseInt((s || '').match(/(\d+)/)?.[1] || 70, 10);

const hl = (text, q) => {
    if (!q.trim()) return text;
    const i = (text || '').toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return text;
    return (
        <>
            {text.slice(0, i)}
            <mark>{text.slice(i, i + q.length)}</mark>
            {text.slice(i + q.length)}
        </>
    );
};

const fmt = (n) => (n || 0).toLocaleString();

const Rail = ({ memories, active, setActive, onNew }) => {
    const masters = memories.filter((m) => m.type !== 'domain');
    const domains = memories.filter((m) => m.type === 'domain');
    const renderItem = (m) => (
        <button
            key={m.id}
            type="button"
            className={`kb-rail-item ${active === m.id ? 'on' : ''}`}
            onClick={() => setActive(m.id)}
            data-tone={m.tone || undefined}
        >
            <span className="kb-rail-flag">{m.flag}</span>
            <span className="kb-rail-body">
                <span className="kb-rail-name">{m.name}</span>
                <span className="kb-rail-sub">{m.pair} · {fmt(m.n)}</span>
            </span>
        </button>
    );
    return (
        <aside className="kb-rail">
            <div className="kb-rail-h">Memories<span className="ct">{memories.length}</span></div>
            <button
                type="button"
                className={`kb-rail-item ${active === 'all' ? 'on' : ''}`}
                onClick={() => setActive('all')}
            >
                <span className="kb-rail-flag">ALL</span>
                <span className="kb-rail-body">
                    <span className="kb-rail-name">All memories</span>
                    <span className="kb-rail-sub">Every pair · {fmt(memories.reduce((s, m) => s + m.n, 0))}</span>
                </span>
            </button>
            {masters.map(renderItem)}
            {domains.length > 0 && <div className="kb-rail-divide" />}
            {domains.map(renderItem)}
            <div className="kb-rail-divide" />
            <button type="button" className="kb-rail-add" onClick={onNew}>{ICN.plus} New memory</button>
        </aside>
    );
};

const TONES = [
    { id: 'accent', color: 'var(--accent, #4f46e5)' },
    { id: 'indigo', color: '#6366f1' },
];

const NewMemorySheet = ({ open, onClose, onCreate, existing, availablePairs, creating, error }) => {
    const [name, setName] = useState('');
    const [type, setType] = useState('master');
    const [pairs, setPairs] = useState([]);
    const [tone, setTone] = useState('accent');
    const [populate, setPopulate] = useState('empty');
    const [cloneOf, setCloneOf] = useState('');
    const [importFile, setImportFile] = useState(null);
    const [writeBack, setWriteBack] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [priority, setPriority] = useState(200);
    const [localErr, setLocalErr] = useState(null);

    // Reset on open
    useEffect(() => {
        if (open) {
            setName('');
            setType('master');
            setPairs([]);
            setTone('accent');
            setPopulate('empty');
            setCloneOf('');
            setImportFile(null);
            setWriteBack(true);
            setReadOnly(false);
            setPriority(200);
            setLocalErr(null);
        }
    }, [open]);

    // Adjust defaults when populate or type changes
    useEffect(() => {
        if (populate === 'import') {
            setWriteBack(false);
            setReadOnly(true);
        } else if (populate === 'empty' || populate === 'project') {
            setWriteBack(true);
            setReadOnly(false);
        } else if (populate === 'clone') {
            setWriteBack(true);
            setReadOnly(false);
        }
    }, [populate]);

    useEffect(() => {
        setPriority(type === 'domain' ? 50 : 200);
    }, [type]);

    const togglePair = (p) => {
        setPairs((cur) => {
            if (type === 'master') return [p];
            return cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p];
        });
    };

    const handleSubmit = () => {
        if (!name.trim()) { setLocalErr('Please give the memory a name.'); return; }
        if (pairs.length === 0) { setLocalErr('Pick at least one language pair.'); return; }
        let populatedFrom = populate;
        let initialCount = 0;
        if (populate === 'clone' && cloneOf) {
            const src = existing.find((m) => m.id === cloneOf);
            initialCount = src ? src.n : 0;
            populatedFrom = { cloneOf };
        } else if (populate === 'import' && importFile) {
            // Stub: derive a plausible count from file size — replaced by real parser later.
            initialCount = Math.max(1, Math.round(importFile.size / 120));
            populatedFrom = 'import';
        } else if (populate === 'project') {
            initialCount = 0;
            populatedFrom = { project: 'stub' };
        }
        onCreate({
            name: name.trim(),
            type,
            pairs,
            tone: type === 'domain' ? tone : null,
            writeBack,
            readOnly,
            priority: Number(priority),
            populatedFrom,
            initialCount,
        });
    };

    if (!open) return null;
    const masterCount = existing.filter((m) => m.type !== 'domain').length;
    const domainCount = existing.filter((m) => m.type === 'domain').length;
    const priorityHint = type === 'domain'
        ? `Lower priority is checked first. Default ${priority} places it above the ${masterCount} master memor${masterCount === 1 ? 'y' : 'ies'}.`
        : `Lower priority is checked first. Default ${priority} places it below the ${domainCount} domain overla${domainCount === 1 ? 'y' : 'ys'}.`;
    const errMsg = localErr || error;
    return (
        <div className="kb-sheet-scrim" onClick={onClose}>
            <div className="kb-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="kb-sheet-head">
                    <h3>New memory</h3>
                    <p>A memory is a scoped, language-paired store of approved source→target segments.</p>
                </div>
                <div className="kb-sheet-body">
                    <div className="kb-field">
                        <label className="kb-field-lbl">Name</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. EN → JA — product UI" autoFocus />
                    </div>
                    <div className="kb-field">
                        <label className="kb-field-lbl">Type</label>
                        <div className="kb-seg">
                            <button type="button" className={type === 'master' ? 'on' : ''} onClick={() => setType('master')}>Language master</button>
                            <button type="button" className={type === 'domain' ? 'on' : ''} onClick={() => setType('domain')}>Domain overlay</button>
                        </div>
                        {type === 'domain' && (
                            <div className="kb-tone-row" style={{ marginTop: 8 }}>
                                {TONES.map((t) => (
                                    <button key={t.id} type="button"
                                        className={tone === t.id ? 'on' : ''}
                                        style={{ background: t.color }}
                                        title={t.id}
                                        onClick={() => setTone(t.id)} />
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="kb-field">
                        <label className="kb-field-lbl">
                            Language pair{type === 'domain' ? 's' : ''}
                        </label>
                        {availablePairs.length === 0 ? (
                            <div className="kb-priority-hint">No pairs yet — translate a project first.</div>
                        ) : (
                            <div className="kb-multi">
                                {availablePairs.map((p) => (
                                    <span key={p}
                                        className={`kb-chip ${pairs.includes(p) ? 'on' : ''}`}
                                        onClick={() => togglePair(p)}>
                                        {p.toUpperCase().replace('->', ' → ')}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="kb-field">
                        <label className="kb-field-lbl">Populate</label>
                        <div className="kb-radio-grp">
                            <label className={`kb-radio ${populate === 'empty' ? 'on' : ''}`}>
                                <input type="radio" name="pop" checked={populate === 'empty'} onChange={() => setPopulate('empty')} />
                                <span>Start empty<span className="sub">A fresh memory — fills as approved segments land.</span></span>
                            </label>
                            <label className={`kb-radio ${populate === 'import' ? 'on' : ''}`}>
                                <input type="radio" name="pop" checked={populate === 'import'} onChange={() => setPopulate('import')} />
                                <span>Import file (TMX / CSV / XLIFF)
                                    <span className="sub">Defaults to read-only · write-back off.</span>
                                    {populate === 'import' && (
                                        <input type="file" accept=".tmx,.csv,.xliff,.xml"
                                            style={{ marginTop: 6 }}
                                            onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
                                    )}
                                </span>
                            </label>
                            <label className={`kb-radio ${populate === 'clone' ? 'on' : ''}`}>
                                <input type="radio" name="pop" checked={populate === 'clone'} onChange={() => setPopulate('clone')} />
                                <span>Clone existing
                                    {populate === 'clone' && (
                                        <select style={{ marginTop: 6, width: '100%' }}
                                            value={cloneOf} onChange={(e) => setCloneOf(e.target.value)}>
                                            <option value="">Select a memory…</option>
                                            {existing.map((m) => (
                                                <option key={m.id} value={m.id}>{m.name} ({fmt(m.n)})</option>
                                            ))}
                                        </select>
                                    )}
                                </span>
                            </label>
                            <label className={`kb-radio ${populate === 'project' ? 'on' : ''}`}>
                                <input type="radio" name="pop" checked={populate === 'project'} onChange={() => setPopulate('project')} />
                                <span>Build from project<span className="sub">Harvest approved segments from a project (stubbed).</span></span>
                            </label>
                        </div>
                    </div>
                    <div className="kb-toggle">
                        <span className="lbl">Write-back<span className="sub">Approved segments flow into this memory.</span></span>
                        <input type="checkbox" checked={writeBack} onChange={(e) => setWriteBack(e.target.checked)} />
                    </div>
                    <div className="kb-toggle">
                        <span className="lbl">Reference-only<span className="sub">Leverage but never modify.</span></span>
                        <input type="checkbox" checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} />
                    </div>
                    <div className="kb-field">
                        <label className="kb-field-lbl">Priority slot</label>
                        <input type="number" value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                            style={{ padding: '8px 10px', border: '1px solid var(--line, rgba(0,0,0,0.12))', borderRadius: 8, font: 'inherit', fontSize: 13 }} />
                        <div className="kb-priority-hint">{priorityHint}</div>
                    </div>
                    {errMsg && <div className="kb-err">{errMsg}</div>}
                </div>
                <div className="kb-sheet-foot">
                    <button type="button" className="kb-btn-secondary" onClick={onClose} disabled={creating}>Cancel</button>
                    <button type="button" className="kb-btn-primary" onClick={handleSubmit} disabled={creating}>
                        {creating ? 'Creating…' : 'Create memory'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const relTime = (iso) => {
    if (!iso) return { val: '—', sub: 'no entries yet' };
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return { val: '—', sub: 'no entries yet' };
    const diff = Math.max(0, Date.now() - then);
    const s = Math.round(diff / 1000);
    if (s < 60) return { val: 'just now', sub: 'last segment saved moments ago' };
    const m = Math.round(s / 60);
    if (m < 60) return { val: `${m}m`, sub: `last segment saved ${m} minute${m === 1 ? '' : 's'} ago` };
    const h = Math.round(m / 60);
    if (h < 24) return { val: `${h}h`, sub: `last segment saved ${h} hour${h === 1 ? '' : 's'} ago` };
    const d = Math.round(h / 24);
    if (d < 30) return { val: `${d}d`, sub: `last segment saved ${d} day${d === 1 ? '' : 's'} ago` };
    const mo = Math.round(d / 30);
    return { val: `${mo}mo`, sub: `last segment saved ${mo} month${mo === 1 ? '' : 's'} ago` };
};

const Stats = ({ total, memoryCount, languageCount, avgConfidence, lastUpdatedAt }) => {
    const last = relTime(lastUpdatedAt);
    return (
        <div className="kb-stats">
            <div className="kb-stat">
                <div className="kb-stat-lbl"><span className="ic">{ICN.layers}</span>Total segments</div>
                <div className="kb-stat-val">{total >= 1000 ? `${(total / 1000).toFixed(1)}k` : total}</div>
                <div className="kb-stat-sub">across {memoryCount} memories · {languageCount} languages</div>
            </div>
            <div className="kb-stat">
                <div className="kb-stat-lbl"><span className="ic green">{ICN.bolt}</span>Avg. match confidence</div>
                <div className="kb-stat-val">{avgConfidence != null ? avgConfidence : '—'}<span className="kb-stat-unit">%</span></div>
                <div className="kb-stat-sub">mean confidence score across stored segments</div>
            </div>
            <div className="kb-stat">
                <div className="kb-stat-lbl"><span className="ic indigo">{ICN.globe}</span>Languages</div>
                <div className="kb-stat-val">{languageCount}</div>
                <div className="kb-stat-sub">distinct language pairs in memory</div>
            </div>
            <div className="kb-stat">
                <div className="kb-stat-lbl"><span className="ic yellow">{ICN.clock}</span>Last update</div>
                <div className="kb-stat-val">{last.val}</div>
                <div className="kb-stat-sub">{last.sub}</div>
            </div>
        </div>
    );
};

const Bar = ({ q, setQ, facets, cycle, pairs }) => (
    <div className="kb-bar">
        <div className="kb-search">
            {ICN.search}
            <input placeholder="Concordance search — find any source or target…" value={q} onChange={(e) => setQ(e.target.value)} />
            <span className="kbd">/</span>
        </div>
        {[['pair', 'Pair', pairs], ['thresh', 'Match', THRESH], ['sort', 'Sort', SORT]].map(([k, label, opts]) => {
            const v = facets[k] || opts[0];
            const applied = k !== 'sort' && v !== opts[0];
            return (
                <button
                    key={k}
                    type="button"
                    className={`kb-facet ${applied ? 'applied' : ''}`}
                    onClick={() => cycle(k, opts)}
                >
                    <span className="lbl-k">{label}:</span>
                    <span className="lbl-v">{v}</span>
                    {ICN.caret}
                </button>
            );
        })}
    </div>
);

const List = ({ rows, q, total, page, setPage, pageSize }) => {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    return (
        <div className="kb-tbl">
            <div className="kb-tm-row head">
                <span>Match</span>
                <span>Source</span>
                <span>Target</span>
                <span className="r">Context · usage</span>
            </div>
            {rows.length === 0 && (
                <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
                    No matches yet. Translate something to populate your memory.
                </div>
            )}
            {rows.map((e) => (
                <div className="kb-tm-row body" key={e.id}>
                    <div className="kb-tm-match">
                        <span className={`kb-tm-pct ${e.cls}`}>{e.pct}%</span>
                        <span className="kb-tm-pair">{e.pair}</span>
                    </div>
                    <div className="kb-tm-cell src">
                        <span className="lang">source</span>
                        <div className="kb-tm-text">{hl(e.src, q)}</div>
                    </div>
                    <div className="kb-tm-cell">
                        <span className="lang">{e.lang} · target</span>
                        <div className="kb-tm-text">{e.tgt}</div>
                    </div>
                    <div className="kb-tm-meta">
                        {e.ctx && <span className="kb-tm-ctx"><span className="dot" /><span>{e.ctx}</span></span>}
                        <span className="kb-tm-usage">used <b>{e.usage}×</b></span>
                    </div>
                </div>
            ))}
            <div className="kb-foot">
                <span className="ct">Showing <b>{rows.length}</b> of <b>{fmt(total)}</b> segments</span>
                <span className="spacer" />
                <button type="button" className="pg-btn" disabled={page === 0} onClick={() => setPage(page - 1)}>‹ Prev</button>
                {Array.from({ length: Math.min(totalPages, 4) }).map((_, i) => (
                    <span key={i} className={`pg-num ${i === page ? 'on' : ''}`} onClick={() => setPage(i)} role="button" tabIndex={0}>
                        {i + 1}
                    </span>
                ))}
                <button type="button" className="pg-btn" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next ›</button>
            </div>
        </div>
    );
};

const TranslationMemoryPage = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [active, setActive] = useState('all');
    const [q, setQ] = useState('');
    const [facets, setFacets] = useState({ pair: 'Any pair', thresh: '≥ 70%', sort: 'Match %' });
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({ memories: [], entries: [], total: 0, filteredTotal: 0 });
    const [reloadKey, setReloadKey] = useState(0);
    const [importing, setImporting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [actionMsg, setActionMsg] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createErr, setCreateErr] = useState(null);
    const fileInputRef = useRef(null);

    const pairs = useMemo(
        () => ['Any pair', ...data.memories.map((m) => m.pair.replace(' → ', '→'))],
        [data.memories],
    );

    // Pairs available for "New memory" — drawn from existing memories, normalized.
    // Falls back to common pairs so a first-time user can still create a memory.
    const availablePairs = useMemo(() => {
        const set = new Set();
        data.memories.forEach((m) => (m.pairs || []).forEach((p) => set.add(p)));
        if (set.size === 0) {
            ['en->de', 'en->fr', 'en->es', 'en->ja', 'en->zh', 'en->pt'].forEach((p) => set.add(p));
        }
        return Array.from(set).sort();
    }, [data.memories]);

    const cycle = (k, opts) => {
        setFacets((f) => {
            const i = opts.indexOf(f[k] || opts[0]);
            return { ...f, [k]: opts[(i + 1) % opts.length] };
        });
        setPage(0);
    };

    useEffect(() => {
        if (!token) return;
        setLoading(true);
        const pair = facets.pair === 'Any pair' ? '' : facets.pair.replace('→', '->').toLowerCase();
        const params = new URLSearchParams({
            q,
            pair,
            memoryId: active !== 'all' ? active : '',
            minMatch: threshNum(facets.thresh),
            page,
            limit: 25,
        });
        axios
            .get(`/api/translation-memory/browse?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setData(r.data);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load translation memory.');
                    setLoading(false);
                }
            });
    }, [token, q, facets, page, active, data.memories.length, dispatch, navigate, reloadKey]);

    const flashMsg = (msg) => {
        setActionMsg(msg);
        setTimeout(() => setActionMsg(null), 4000);
    };

    const handleExport = async () => {
        if (!token || exporting) return;
        setExporting(true);
        try {
            const scopedMemory = active !== 'all'
                ? data.memories.find((m) => m.id === active)
                : null;
            const params = new URLSearchParams();
            if (scopedMemory) params.set('memoryId', scopedMemory.id);
            const qs = params.toString();
            const r = await axios.get(`/api/translation-memory/download${qs ? `?${qs}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const filename = (() => {
                const cd = r.headers?.['content-disposition'] || '';
                const m = /filename="?([^";]+)"?/.exec(cd);
                if (m) return m[1];
                if (scopedMemory) {
                    return `${scopedMemory.name.toLowerCase().replace(/[^a-z0-9-_]+/g, '-').replace(/^-+|-+$/g, '') || 'memory'}.tmx`;
                }
                return 'translation-memory.tmx';
            })();
            const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/xml' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            flashMsg({ kind: 'ok', text: scopedMemory ? `Exported "${scopedMemory.name}".` : 'Translation memory exported.' });
        } catch (err) {
            const status = err.response?.status;
            let text = 'Export failed.';
            if (status === 404) text = 'No translation memory to export yet.';
            else if (status === 403) text = 'TMX export is not available on your plan.';
            flashMsg({ kind: 'err', text });
        } finally {
            setExporting(false);
        }
    };

    const handleImportClick = () => {
        if (!importing) fileInputRef.current?.click();
    };

    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !token) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            await axios.post('/api/translation-memory/upload', fd, {
                headers: { Authorization: `Bearer ${token}` },
            });
            flashMsg({ kind: 'ok', text: 'TMX imported.' });
            setReloadKey((k) => k + 1);
        } catch (err) {
            const text = err.response?.data?.msg || 'Import failed.';
            flashMsg({ kind: 'err', text });
        } finally {
            setImporting(false);
        }
    };

    const languageCount = new Set(data.memories.flatMap((m) => m.pairs || [m.pair])).size;

    const handleCreateMemory = async (payload) => {
        if (!token) return;
        setCreating(true);
        setCreateErr(null);
        try {
            const r = await axios.post('/api/translation-memory/memories', payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSheetOpen(false);
            const newId = r.data?.memory?.id;
            setReloadKey((k) => k + 1);
            if (newId) {
                setActive(newId);
                setPage(0);
            }
            flashMsg({ kind: 'ok', text: 'Memory created.' });
        } catch (err) {
            const text = err.response?.data?.msg || 'Failed to create memory.';
            setCreateErr(text);
        } finally {
            setCreating(false);
        }
    };

    const activeMemory = active !== 'all'
        ? data.memories.find((m) => m.id === active)
        : null;
    const showEmptyMemory = activeMemory && activeMemory.n === 0 && data.entries.length === 0;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Translation memory</h1>
                <span className="rs-page-title-count">{fmt(data.total)}</span>
                <div className="rs-page-actions">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".tmx,.xml,application/xml,text/xml"
                        style={{ display: 'none' }}
                        onChange={handleImportFile}
                    />
                    <button type="button" className="btn-q" onClick={handleImportClick} disabled={importing}>
                        {ICN.upload}<span>{importing ? 'Importing…' : 'Import TMX'}</span>
                    </button>
                    <button
                        type="button"
                        className="btn-q"
                        onClick={handleExport}
                        disabled={exporting}
                        title={active !== 'all'
                            ? `Exports "${data.memories.find((m) => m.id === active)?.name || 'selected memory'}" only`
                            : 'Exports every memory'}
                    >
                        {ICN.download}
                        <span>
                            {exporting
                                ? 'Exporting…'
                                : (active !== 'all' ? 'Export memory' : 'Export all')}
                        </span>
                    </button>
                </div>
            </div>
            <p className="rs-page-sub">Every translation your team has shipped — searchable, leverage-tracked.</p>
            {actionMsg && (
                <div
                    style={{
                        margin: '0 0 12px',
                        padding: '8px 12px',
                        borderRadius: 8,
                        fontSize: 13,
                        background: actionMsg.kind === 'ok' ? 'rgba(34,197,94,0.12)' : 'rgba(220,38,38,0.12)',
                        color: actionMsg.kind === 'ok' ? '#15803d' : '#b91c1c',
                    }}
                >
                    {actionMsg.text}
                </div>
            )}

            <Stats
                total={data.total}
                memoryCount={data.memories.length}
                languageCount={languageCount}
                avgConfidence={data.avgConfidence}
                lastUpdatedAt={data.lastUpdatedAt}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16, alignItems: 'start' }}>
                <Rail
                    memories={data.memories}
                    active={active}
                    setActive={(id) => { setActive(id); setPage(0); }}
                    onNew={() => { setCreateErr(null); setSheetOpen(true); }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Bar q={q} setQ={(v) => { setQ(v); setPage(0); }} facets={facets} cycle={cycle} pairs={pairs} />
                    {loading ? (
                        <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading entries…</div>
                    ) : error ? (
                        <div style={{ padding: 40, color: 'crimson' }}>{error}</div>
                    ) : showEmptyMemory ? (
                        <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)', border: '1px dashed var(--line, rgba(0,0,0,0.12))', borderRadius: 10 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', marginBottom: 6 }}>{activeMemory.name} is empty</div>
                            <div style={{ fontSize: 13 }}>Import or start translating to populate this memory.</div>
                        </div>
                    ) : (
                        <List rows={data.entries} q={q} total={data.filteredTotal || data.total} page={page} setPage={setPage} pageSize={25} />
                    )}
                </div>
            </div>
            <NewMemorySheet
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                onCreate={handleCreateMemory}
                existing={data.memories}
                availablePairs={availablePairs}
                creating={creating}
                error={createErr}
            />
        </>
    );
};

export default TranslationMemoryPage;
